import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  EPropertyType,
  type IProperty,
  type IPropertyValue,
  type ITranslation,
} from '@mahawi/eshop-common/src/types';
import { createSlice } from '@reduxjs/toolkit';

import {
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
} from '../async-state';
import { type IPropertyState } from './types';

export const preloadedState: IPropertyState = {
  inProcess: false,
  property: undefined,
};

const propertySlice = createSlice({
  name: 'Property',
  initialState: preloadedState,
  reducers: {
    propertyLoad: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(code: string) {
        return { payload: { property: { code } } };
      },
    },
    propertyLoadResponse: {
      reducer(state, action: { payload: { property: IProperty | null } }) {
        state.inProcess = false;
        state.property = action.payload.property;

        state.property?.values?.sort(
          (a: IPropertyValue, b: IPropertyValue): number =>
            stringCompare(a.code, b.code),
        );
      },
      prepare(property: IProperty | null) {
        return { payload: { property } };
      },
    },
    nameUpdate: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(propertyCode: string, name: string, languageTypeCode: string) {
        return {
          payload: {
            property: { code: propertyCode, name },
            languageType: { code: languageTypeCode },
          },
        };
      },
    },
    nameUpdateResponse: {
      reducer(state) {
        state.inProcess = false;
      },
      prepare(propertyCode: string, name: string) {
        return { payload: { property: { code: propertyCode, name } } };
      },
    },
    namesUpdate: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(propertyCode: string, names: ITranslation[]) {
        return { payload: { property: { code: propertyCode }, names } };
      },
    },
    namesUpdateResponse: {
      reducer(state) {
        state.inProcess = false;
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    nameValueUpdate: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(
        propertyValueCode: string,
        name: string,
        languageTypeCode: string,
      ) {
        return {
          payload: {
            propertyValue: { code: propertyValueCode, name },
            languageType: { code: languageTypeCode },
          },
        };
      },
    },
    nameValueUpdateResponse: {
      reducer(state) {
        state.inProcess = false;
      },
      prepare(saved: boolean) {
        return { payload: { saved } };
      },
    },
    nameLive: {
      reducer(
        state,
        action: {
          payload: {
            property: { code: string; name: string };
            languageType: { code: string };
          };
        },
      ) {
        const { payload } = action;
        const name: ITranslation | undefined = state.property?.names.find(
          ({ code }: ITranslation): boolean =>
            code === payload.languageType.code,
        );

        if (name) {
          name.value = payload.property.name;
        } else {
          state.property?.names.push({
            code: payload.languageType.code,
            value: payload.property.name,
          });
        }
      },
      prepare(propertyCode: string, name: string, languageTypeCode: string) {
        return {
          payload: {
            property: { code: propertyCode, name },
            languageType: { code: languageTypeCode },
          },
        };
      },
    },
    propertyValueNameLive: {
      reducer(
        state,
        action: {
          payload: {
            propertyValue: { code: string; name: string };
            languageType: { code: string };
          };
        },
      ) {
        const { payload } = action;
        const propertyValue: IPropertyValue | undefined =
          state.property?.values?.find(
            (p: IPropertyValue): boolean =>
              p.code === payload.propertyValue.code,
          );

        if (propertyValue) {
          const name: ITranslation | undefined = propertyValue.names.find(
            ({ code }: ITranslation): boolean =>
              code === payload.languageType.code,
          );

          if (name) {
            name.value = payload.propertyValue.name;
          } else {
            propertyValue.names.push({
              code: payload.languageType.code,
              value: payload.propertyValue.name,
            });
          }
        }
      },
      prepare(
        propertyValueCode: string,
        name: string,
        languageTypeCode: string,
      ) {
        return {
          payload: {
            propertyValue: { code: propertyValueCode, name },
            languageType: { code: languageTypeCode },
          },
        };
      },
    },
    updateUnit: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(propertyCode: string, unit: string) {
        return { payload: { property: { code: propertyCode, unit } } };
      },
    },
    updateValueNames: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(propertyValueCode: string, names: ITranslation[]) {
        return {
          payload: { propertyValue: { code: propertyValueCode }, names },
        };
      },
    },
    updateValueNamesResponse: {
      reducer(state) {
        state.inProcess = false;
      },
      prepare(updated: boolean) {
        return { payload: { updated } };
      },
    },
    updateType: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(propertyCode: string, type: EPropertyType) {
        return { payload: { property: { code: propertyCode, type } } };
      },
    },
    propertySaveWarehouseProperties: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(propertyCode: string, warehousePropertiesRawNames: string[]) {
        return {
          payload: {
            property: { code: propertyCode },
            warehouse: { properties: warehousePropertiesRawNames },
          },
        };
      },
    },
    saveCategoriesUUIDs: {
      reducer(state) {
        state.inProcess = true;
      },
      prepare(propertyCode: string, categoriesUUIDs: string[]) {
        return {
          payload: { property: { code: propertyCode }, categoriesUUIDs },
        };
      },
    },
    saveCategoriesUUIDsResponse: {
      reducer(state, action: { payload: { property: IProperty | null } }) {
        state.inProcess = false;
        state.property = action.payload.property;
      },
      prepare(property: IProperty | null) {
        return { payload: { property } };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      requestFailedMatcher('Property'),
      resetLoadingOnRequestFailed,
    );
  },
});

export const {
  propertyLoad,
  propertyLoadResponse,
  nameUpdate,
  nameUpdateResponse,
  namesUpdate,
  namesUpdateResponse,
  nameValueUpdate,
  nameValueUpdateResponse,
  nameLive,
  propertyValueNameLive,
  updateUnit,
  updateValueNames,
  updateValueNamesResponse,
  updateType,
  propertySaveWarehouseProperties,
  saveCategoriesUUIDs,
  saveCategoriesUUIDsResponse,
} = propertySlice.actions;
export default propertySlice.reducer;
